import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Row,
  Col,
  Alert,
  Spinner,
  Collapse,
  FormGroup,
  Label,
  Progress,
} from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import FeatherIcon from 'feather-icons-react';
import {
  getSignedUrls,
  uploadAttachmentToInspection,
  sendInspectionReport,
  deleteBuildingInspection,
} from './FormikHelpers/Api';
import { useDraftAutosave } from '../../../../../hooks/useDraftAutosave';
import { clearDraftBackup } from '../../../../../hooks/draftBackup';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import './buildinginspection.scss';
import { validateStep, validateDraft } from './FormikHelpers';
import { DownloadInspectionPDFButton } from './BuildingInspectionPDFTemplate';

const defaultItems = {
  interior: [
    "Lobby", "Hallways", "Carpets", "Walls", "Interior Hallway Doors",
    "Interior Lighting", "Suite Doors from Outside", "Ceiling", "Staircase",
    "Garbage Room", "Laundry Room"
  ],
  elevators: [
    "No Sign of Damage", "Tracks Free of Debris", "Doors Operating Smoothly",
    "No Jerk or Weird Sound"
  ],
  underground: [
    "Sprinkler Room", "Elevator Room with Clear Access", "Maintenance Room",
    "Electrical Room", "Boiler Room", "Janitor Room", "Make Up Air Area",
    "UG Storage Cages", "UG Parking Door"
  ],
  outside: [
    "Landscaping/Snow Removal", "Sidewalks", "Fences", "Garbage Area",
    "Outdoor Parking", "Dog Run", "Roof Damages", "Side Entrance Doors",
    "Building Appearance", "Sides of Building", "Windows Closed (in winter)",
    "Patios of Residents"
  ],
  misc_others: [
    "Amenities (Fitness/Theatre/Lounge)", "Office"
  ]
};

const ALLOWED_FILE_TYPES = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/mov,video/webm,video/x-msvideo,video/x-matroska';
const MAX_FILE_SIZE = 120 * 1024 * 1024;

const MediaPreviewModal = ({ isOpen, toggle, media }) => {
  if (!media) return null;
  const { url, name, isVideo } = media;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="xl"
      backdrop="static"
      style={{ zIndex: 9999 }}
      className="preview-modal"
    >
      <ModalHeader toggle={toggle} className="border-0 pb-1">
        {isVideo ? 'Video Preview' : 'Image Preview'} — {name || 'Media'}
      </ModalHeader>
      <ModalBody className="text-center py-4 bg-dark">
        {isVideo ? (
          <video src={url} controls autoPlay style={{ maxHeight: '75vh', maxWidth: '100%', borderRadius: '12px' }} />
        ) : (
          <img src={url} alt={name || "Preview"} style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain', borderRadius: '12px' }} />
        )}
      </ModalBody>
      <ModalFooter className="border-0 justify-content-center">
        <Button color="light" onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

const InspectionRow = ({
  section,
  itemIndex,
  item,
  formik,
  pendingUploads,
  setPendingUploads,
  signedUrlCache,
  setSelectedMedia,
  isCustomItem,
  isEdit,
  userRole,
  userId,
  isReadOnly = false,
}) => {
  const key = `${section}_${itemIndex}`;
  const pending = pendingUploads[key] || [];
  const needsWork = item.needs_work === true;
  const isItemCompleted = item.completed === true;

  const isCompanyUser = userRole === 'company';
  const canEditFully = !isEdit || isCompanyUser;

  const canChangeNeedsWork = canEditFully && !isReadOnly;
  const canChangeDueDate = canEditFully && needsWork && !isReadOnly;
  const canChangeName = canEditFully && isCustomItem && !isReadOnly;
  const canDeleteItem = canEditFully && isCustomItem && !isReadOnly;
  const canChangeComments = !isReadOnly;
  const canChangeCompleted = (isEdit ? true : canEditFully) && !isReadOnly;
  const canAddAttachments = canEditFully && !isReadOnly;
  const canRemoveAttachments = canEditFully && !isReadOnly;
  const canAddMemo = canEditFully && !isReadOnly;
  const [showMemos, setShowMemos] = useState(false);
  const [newMemoText, setNewMemoText] = useState('');

  const memos = item.memos || [];

  const handleAddMemo = () => {
    if (!newMemoText.trim() || !canAddMemo) return;

    const optimisticMemo = {
      content: newMemoText.trim(),
      createdBy: { role: userRole || 'user', object_id: userId || null },
      createdAt: new Date().toISOString(),
    };

    const currentMemos = [...(item.memos || [])];
    formik.setFieldValue(`${section}[${itemIndex}].memos`, [...currentMemos, optimisticMemo]);
    setNewMemoText('');
  };

  const memoDate = (isoString) => {
    try {
      return new Date(isoString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (!canAddAttachments) {
      return;
    }
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(({ file, errors }) => {
        errors.forEach(error => {
          if (error.code === 'file-too-large') toast.error(`${file.name} is too large (max 120MB)`);
          else if (error.code === 'file-invalid-type') toast.error(`${file.name}: invalid file type`);
        });
      });
    }
    if (!acceptedFiles.length) return;

    setPendingUploads(prev => ({
      ...prev,
      [key]: [
        ...(prev[key] || []),
        ...acceptedFiles.map(file => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
          file,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
          name: file.name,
          type: file.type,
          size: file.size,
          uploading: false,
          error: false,
        })),
      ],
    }));
  }, [key, setPendingUploads, canAddAttachments]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ALLOWED_FILE_TYPES,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    disabled: !canAddAttachments,
  });

  const removeUploadedAttachment = async (idx) => {
    if (!canRemoveAttachments) return;
    const result = await Swal.fire({
      title: 'Delete Attachment?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
    });

    if (result.isConfirmed) {
      const attachments = [...(item.attachments || [])];
      attachments.splice(idx, 1);
      formik.setFieldValue(`${section}[${itemIndex}].attachments`, attachments);
    }
  };

  const removePendingAttachment = (idx) => {
    if (!canRemoveAttachments) return;
    setPendingUploads(prev => {
      const newPending = [...(prev[key] || [])];
      if (newPending[idx]?.uploading) return prev;
      if (newPending[idx]?.preview) URL.revokeObjectURL(newPending[idx].preview);
      newPending.splice(idx, 1);
      return { ...prev, [key]: newPending };
    });
  };

  const openPreview = (url, name, isVideo) => {
    setSelectedMedia({ url, name, isVideo });
  };

  return (
    <>
      <tr>
        <td style={{ verticalAlign: 'middle' }}>
          <div className="d-flex align-items-center gap-2">
            <Input
              type="text"
              placeholder="Category name"
              value={item.name || ''}
              onChange={e => formik.setFieldValue(`${section}[${itemIndex}].name`, e.target.value)}
              disabled={!canChangeName}
            />
            {canDeleteItem && (
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => {
                  const current = [...formik.values[section]];
                  current.splice(itemIndex, 1);
                  formik.setFieldValue(section, current);
                }}
              >
                <i className="bx bx-trash"></i>
              </button>
            )}
          </div>
        </td>

        <td className="text-center">
          <div className="form-check form-switch form-switch-lg d-flex justify-content-center">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={needsWork}
              onChange={() => {
                if (!canChangeNeedsWork) return;
                const newNeedsWork = needsWork ? null : true;
                formik.setFieldValue(`${section}[${itemIndex}].needs_work`, newNeedsWork);
                if (newNeedsWork !== true) {
                  formik.setFieldValue(`${section}[${itemIndex}].due_date`, '');
                  formik.setFieldValue(`${section}[${itemIndex}].completed`, false);
                }
              }}
              disabled={!canChangeNeedsWork}
            />
          </div>
        </td>

        <td className="text-center">
          <Input
            type="date"
            min={new Date().toISOString().split('T')[0]}
            value={item.due_date ? new Date(item.due_date).toISOString().split('T')[0] : ''}
            onChange={e => formik.setFieldValue(`${section}[${itemIndex}].due_date`, e.target.value || null)}
            disabled={!canChangeDueDate}
          />
        </td>

        <td className="text-center">
          <FormGroup className="mb-0">
            <Input
              type="select"
              value={isItemCompleted ? 'done' : 'pending'}
              onChange={e =>
                formik.setFieldValue(
                  `${section}[${itemIndex}].completed`,
                  e.target.value === 'done'
                )
              }
              disabled={!canChangeCompleted}
              bsSize="sm"
            >
              <option value="pending">Pending</option>
              <option value="done">Done</option>
            </Input>
          </FormGroup>
        </td>

        <td>
          <Input
            type="text"
            placeholder="Notes / description"
            value={item.comments || ''}
            onChange={e => formik.setFieldValue(`${section}[${itemIndex}].comments`, e.target.value)}
            disabled={!canChangeComments}
          />
        </td>

        <td style={{ minWidth: '280px' }}>
          <div className="d-flex flex-column gap-2">
            {/* Pending uploads */}
            {pending.map((media, i) => (
              <div
                key={`pending-${i}`}
                className="d-flex align-items-center p-2 bg-light rounded border media-preview-item"
                style={{ cursor: media.preview ? 'pointer' : 'default' }}
                onClick={() => media.preview && openPreview(media.preview, media.name, media.type?.startsWith('video/'))}
              >
                <div className="me-2 rounded overflow-hidden bg-dark" style={{ width: 64, height: 48 }}>
                  {media.type?.startsWith('image/') && media.preview && (
                    <img src={media.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  {media.type?.startsWith('video/') && (
                    <div className="text-white h-100 d-flex align-items-center justify-content-center">
                      <i className="bx bx-video fs-4"></i>
                    </div>
                  )}
                </div>
                <div className="flex-grow-1">
                  <div className="small fw-medium text-truncate">{media.name}</div>
                  <small className={media.error ? 'text-danger' : 'text-muted'}>
                    {media.uploading
                      ? 'Uploading…'
                      : media.error
                        ? 'Upload failed, will retry on save'
                        : `${media.type?.startsWith('video/') ? 'Video' : 'Image'} • ${(media.size / 1024 / 1024).toFixed(1)} MB`}
                  </small>
                </div>
                {media.uploading ? (
                  <Spinner size="sm" className="ms-2" />
                ) : canRemoveAttachments && (
                  <button
                    className="btn btn-sm btn-soft-danger ms-2"
                    onClick={e => {
                      e.stopPropagation();
                      removePendingAttachment(i);
                    }}
                  >
                    <i className="bx bx-trash"></i>
                  </button>
                )}
              </div>
            ))}

            {/* Uploaded attachments */}
            {(item.attachments || []).map((key, idx) => {
              const signedUrl = signedUrlCache[key];
              const isVideo = /\.(mp4|mov|webm|avi|mkv)$/i.test(key);

              return (
                <div
                  key={`uploaded-${idx}`}
                  className="d-flex align-items-center p-2 bg-white border rounded position-relative media-preview-item"
                  style={{ cursor: signedUrl ? 'pointer' : 'default' }}
                  onClick={() => signedUrl && openPreview(signedUrl, `Attachment ${idx + 1}`, isVideo)}
                >
                  <div className="me-2 rounded overflow-hidden bg-dark" style={{ width: 64, height: 48 }}>
                    {isVideo ? (
                      <div className="h-100 d-flex align-items-center justify-content-center text-white">
                        <i className="bx bx-video fs-4"></i>
                      </div>
                    ) : signedUrl ? (
                      <img src={signedUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Spinner size="sm" />
                    )}
                  </div>
                  <small className="flex-grow-1 text-muted">
                    {isVideo ? 'Video' : 'Image'} {idx + 1}
                  </small>
                  {canRemoveAttachments && (
                    <button
                      className="btn btn-sm btn-soft-danger ms-2"
                      onClick={e => {
                        e.stopPropagation();
                        removeUploadedAttachment(idx);
                      }}
                    >
                      <i className="bx bx-trash"></i>
                    </button>
                  )}
                </div>
              );
            })}

            {canAddAttachments && (
              <div
                {...getRootProps()}
                className={`btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center ${isDragActive ? 'btn-primary text-white' : ''}`}
              >
                <input {...getInputProps()} />
                <FeatherIcon icon="camera" size={14} className="me-1" />
                {isDragActive ? 'Drop here' : pending.length ? `Add more (${pending.length})` : 'Add Photo/Video'}
              </div>
            )}
          </div>
        </td>

        <td style={{ minWidth: '180px' }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary w-100 d-flex align-items-center justify-content-between"
            onClick={() => setShowMemos(!showMemos)}
            disabled={isReadOnly}
          >
            <span>
              <i className="bx bx-message-dots me-1"></i>
              Memos ({memos.length})
            </span>
            <i className={`bx ${showMemos ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
          </button>
        </td>
      </tr>

      {showMemos && (
        <tr>
          <td colSpan={7} className="p-3 bg-light border-top-0">
            <div className="memos-container" style={{ maxHeight: '240px', overflowY: 'auto' }}>
              {memos.length === 0 ? (
                <div className="text-center text-muted py-4">
                  No memos yet
                </div>
              ) : (
                memos.map((memo, mIdx) => (
                  <div
                    key={mIdx}
                    className="memo-item p-2 mb-2 bg-white rounded border"
                  >
                    <div className="d-flex justify-content-between small text-muted mb-1">
                      <span>
                        <strong>{memo.createdBy?.role || 'User'}</strong>
                      </span>
                      <span>{memoDate(memo.createdAt)}</span>
                    </div>
                    <div className="memo-content" style={{ whiteSpace: 'pre-wrap' }}>
                      {memo.content}
                    </div>
                  </div>
                ))
              )}

              {canAddMemo && (
                <div className="mt-3">
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    placeholder="Add a new memo / note..."
                    value={newMemoText}
                    onChange={e => setNewMemoText(e.target.value)}
                    disabled={isReadOnly}
                  />
                  <div className="text-end mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={!newMemoText.trim() || isReadOnly}
                      onClick={handleAddMemo}
                    >
                      <i className="bx bx-send me-1"></i>
                      Add Memo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Quiet status pill in the modal header.
const AutosaveStatus = ({ status }) => {
  if (!status || status.state === 'off') return null;
  const time = status.savedAt
    ? new Date(status.savedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : '';
  let icon = 'check-circle';
  let text = 'Autosave on';
  let tone = 'ok';
  if (status.state === 'saving') { icon = 'loader'; text = 'Saving draft…'; tone = 'busy'; }
  else if (status.state === 'dirty') { icon = 'edit-2'; text = 'Unsaved changes'; tone = 'busy'; }
  else if (status.state === 'saved') { icon = 'check-circle'; text = time ? `Draft saved ${time}` : 'Draft saved'; tone = 'ok'; }
  else if (status.state === 'error') { icon = 'alert-triangle'; text = 'Autosave failed, retrying'; tone = 'warn'; }
  else if (status.state === 'paused') { icon = 'pause-circle'; text = status.error || 'Autosave paused'; tone = 'warn'; }
  return (
    <span className={`bi-autosave bi-autosave--${tone}`} title={status.error || text}>
      <FeatherIcon icon={icon} size={13} className={status.state === 'saving' ? 'bi-autosave__spin' : ''} />
      <span className="bi-autosave__text">{text}</span>
    </span>
  );
};

const GenerateBuildingInspectionModal = ({
  isModalOpen,
  setIsModalOpen,
  formik,
  isEdit = false,
  onRefresh = () => { },
  userRole = 'other',
  userId = '',
  submitInspection,
  autosaveDraft,
  autoDraftId = null,
  onDraftDiscarded = () => { },
  backupKey = '',
  openCount = 0,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [pendingUploads, setPendingUploads] = useState({});
  const [openAccordion, setOpenAccordion] = useState([0, 1, 2, 3, 4]);
  const [signedUrlCache, setSignedUrlCache] = useState({});
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [closing, setClosing] = useState(false);

  const fetchedKeysRef = useRef(new Set());
  const formikRef = useRef(formik);
  formikRef.current = formik;
  const explicitlySavedRef = useRef(false);
  const bgUploadPromiseRef = useRef(null);
  const uploadingIdsRef = useRef(new Set());
  const isCompleted = formik.values.status === 'completed';
  const isReadOnly = isEdit && isCompleted;
  const isValidProperty = !!formik.values.property_details?.property_id;
  const draftId = formik.values._id || autoDraftId || null;

  // --- Background autosave -------------------------------------------------
  const autosaveEnabled = isModalOpen && isValidProperty && !isReadOnly && typeof autosaveDraft === 'function';
  const serializeForAutosave = useCallback((v) => JSON.stringify({
    d: v.inspection_date,
    p: v.property_details,
    i: v.interior,
    e: v.elevators,
    u: v.underground,
    o: v.outside,
    m: v.misc_others,
    c: v.conclusions_recommendations,
  }), []);
  const runAutosave = useCallback(async (values) => {
    const id = await autosaveDraft(values);
    if (id && !formikRef.current.values._id) {
      formikRef.current.setFieldValue('_id', id, false);
    }
    return id;
  }, [autosaveDraft]);
  const autosave = useDraftAutosave({
    enabled: autosaveEnabled,
    values: formik.values,
    save: runAutosave,
    serialize: serializeForAutosave,
    backupKey,
    resetKey: openCount,
  });

  // Fresh session each time the modal opens.
  useEffect(() => {
    if (!isModalOpen) return;
    explicitlySavedRef.current = false;
    setPendingUploads({});
    setClosing(false);
  }, [isModalOpen, openCount]);

  // Once a draft exists on the server, pending photos and videos go up in the
  // background so they are safe even if the session ends before Save.
  useEffect(() => {
    if (!draftId || isReadOnly || uploading || !isModalOpen) return;
    const batch = [];
    Object.entries(pendingUploads).forEach(([key, files]) => {
      (files || []).forEach((media) => {
        if (media && !media.error && !uploadingIdsRef.current.has(media.id)) batch.push({ key, media });
      });
    });
    if (!batch.length) return;
    batch.forEach(({ media }) => uploadingIdsRef.current.add(media.id));
    const ids = new Set(batch.map((b) => b.media.id));
    setPendingUploads((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, files]) => {
        next[k] = files.map((m) => (ids.has(m.id) ? { ...m, uploading: true, error: false } : m));
      });
      return next;
    });

    const run = async () => {
      for (const { key, media } of batch) {
        const [section, idxStr] = key.split('_');
        const itemIndex = parseInt(idxStr, 10);
        try {
          const { key: uploadedKey } = await uploadAttachmentToInspection(draftId, section, itemIndex, media.file);
          const fk = formikRef.current;
          const current = fk.values[section]?.[itemIndex]?.attachments || [];
          fk.setFieldValue(`${section}[${itemIndex}].attachments`, [...current, uploadedKey]);
          if (media.preview) URL.revokeObjectURL(media.preview);
          setPendingUploads((prev) => ({
            ...prev,
            [key]: (prev[key] || []).filter((m) => m.id !== media.id),
          }));
        } catch (err) {
          console.error(`Background upload failed: ${media.name}`, err);
          setPendingUploads((prev) => ({
            ...prev,
            [key]: (prev[key] || []).map((m) => (m.id === media.id ? { ...m, uploading: false, error: true } : m)),
          }));
        } finally {
          uploadingIdsRef.current.delete(media.id);
        }
      }
    };
    const p = run();
    const prevPromise = bgUploadPromiseRef.current;
    bgUploadPromiseRef.current = prevPromise ? prevPromise.then(() => p) : p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, pendingUploads, isReadOnly, uploading, isModalOpen]);
  useEffect(() => {
    if (!isEdit && formik.values.property_details?.property_id) {
      Object.keys(defaultItems).forEach(section => {
        if (!Array.isArray(formik.values[section]) || formik.values[section].length === 0) {
          const defaults = defaultItems[section].map(name => ({
            name,
            needs_work: null,
            comments: '',
            attachments: [],
            due_date: null,
            completed: false,
            is_custom: false,
          }));
          formik.setFieldValue(section, defaults);
        }
      });
    }
  }, [formik.values.property_details?.property_id, isEdit, formik]);

  useEffect(() => {
    const allKeys = Object.values(formik.values)
      .filter(Array.isArray)
      .flatMap(arr => arr.flatMap(item => item.attachments || []))
      .filter(Boolean);

    const uniqueKeys = [...new Set(allKeys)];
    const missing = uniqueKeys.filter(
      k => !signedUrlCache[k] && !fetchedKeysRef.current.has(k)
    );

    if (!missing.length) return;

    const fetchUrls = async () => {
      missing.forEach(k => fetchedKeysRef.current.add(k));
      try {
        const urls = await getSignedUrls(missing);
        setSignedUrlCache(prev => ({ ...prev, ...urls }));
      } catch (e) {
        toast.error("Could not load some attachment previews");
      }
    };

    fetchUrls();
  }, [formik.values, signedUrlCache]);

  const uploadAllPendingFiles = async (inspectionId) => {
    const allPending = Object.entries(pendingUploads);
    if (!allPending.some(([, files]) => files.length > 0)) return true;

    setUploading(true);
    let total = 0;
    let successCount = 0;
    let failedCount = 0;

    allPending.forEach(([, files]) => (total += files.length));

    setUploadStatus({ current: 0, total, currentFile: "" });

    for (const [key, files] of allPending) {
      if (!files.length) continue;

      const [section, idxStr] = key.split("_");
      const itemIndex = parseInt(idxStr, 10);

      for (const media of files) {
        const { file, name } = media;

        setUploadStatus({
          current: successCount + failedCount + 1,
          total,
          currentFile: name || file.name,
        });

        try {
          const { key: uploadedKey } = await uploadAttachmentToInspection(
            inspectionId,
            section,
            itemIndex,
            file
          );
          const path = `${section}[${itemIndex}].attachments`;
          const current = formik.values[section]?.[itemIndex]?.attachments || [];
          formik.setFieldValue(path, [...current, uploadedKey]);

          successCount++;
        } catch (err) {
          console.error(`Failed: ${file.name}`, err);
          toast.error(`Upload failed: ${file.name}`);
          failedCount++;
        }
      }
    }

    setPendingUploads({});

    if (failedCount > 0) {
      toast.warning(`Uploaded ${successCount}/${total} files (${failedCount} failed)`);
    }
    setUploading(false);
    setUploadStatus(null);

    return failedCount === 0;
  };

  const handleSave = async (shouldSetCompleted = false) => {
    const valid = shouldSetCompleted
      ? await validateStep(formik.values)
      : await validateDraft(formik.values);
    if (!valid) return;
    const submitData = {
      ...formik.values,
      status: shouldSetCompleted ? 'completed' : 'draft',
    };

    setUploading(true);
    try {
      // Never race the background autosave: wait for it, and for any files it
      // is still uploading, so the record is created exactly once.
      await autosave.settle();
      if (bgUploadPromiseRef.current) {
        try { await bgUploadPromiseRef.current; } catch (_) { /* reported per file */ }
      }
      const inspectionId = await submitInspection(submitData);
      if (Object.values(pendingUploads).some(arr => arr.length > 0) && inspectionId) {
        await uploadAllPendingFiles(inspectionId);
      }

      const shouldSendReport =
        submitData.status === 'completed' &&
        (!isEdit || formik.initialValues?.status !== 'completed');

      if (shouldSendReport) {
        await sendInspectionReport(inspectionId);
      }
      formik.setFieldValue('status', submitData.status);
      explicitlySavedRef.current = true;
      autosave.markSaved(formik.values, inspectionId);
      if (backupKey) clearDraftBackup(backupKey);
      setIsModalOpen(false);
      onRefresh();
      toast.success(isEdit ? "Inspection updated" : "Inspection saved");
    } catch (err) {
      toast.error(err.message || "Failed to save inspection");
    } finally {
      setUploading(false);
    }
  };

  // Closing with autosave on: nothing is lost, but a draft this session
  // created without an explicit save is offered for discard.
  const handleClose = async () => {
    if (closing) return;
    if (isReadOnly || !autosaveEnabled) {
      setIsModalOpen(false);
      return;
    }
    setClosing(true);
    try {
      await autosave.flush();
    } catch (_) {
      // status pill already shows it
    }
    const id = formikRef.current.values._id || autoDraftId;
    if (id && !isEdit && !explicitlySavedRef.current) {
      const result = await Swal.fire({
        title: 'Keep this draft?',
        text: 'Your work so far has been saved automatically as a draft. You can pick it up later from the list.',
        icon: 'question',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Keep draft',
        denyButtonText: 'Discard draft',
        cancelButtonText: 'Keep editing',
        confirmButtonColor: '#0e578e',
        denyButtonColor: '#d33',
        reverseButtons: true,
      });
      if (result.isDismissed) {
        setClosing(false);
        return;
      }
      if (result.isDenied) {
        try {
          await deleteBuildingInspection(id);
          onDraftDiscarded();
        } catch (err) {
          toast.error('Could not discard the draft. It stays in the list.');
        }
      }
    }
    if (backupKey) clearDraftBackup(backupKey);
    setClosing(false);
    setIsModalOpen(false);
    onRefresh();
  };

  const handleFinalize = async () => {
    const result = await Swal.fire({
      title: 'Finalize Inspection?',
      text: isEdit
        ? "This will update the inspection and mark it as completed. You won't be able to edit it afterward."
        : "This will create the inspection report and mark it as completed. Changes won't be possible after this.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: isEdit ? 'Yes, Update & Complete' : 'Yes, Create & Finalize',
      cancelButtonText: 'No, keep editing',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      await handleSave(true);
    }
  };

  const addNewItem = (section) => {
    const current = formik.values[section] || [];
    formik.setFieldValue(section, [
      ...current,
      {
        name: '',
        needs_work: null,
        comments: '',
        attachments: [],
        due_date: null,
        completed: false,
        is_custom: true
      }
    ]);
  };

  const sectionTitles = {
    interior: 'INTERIOR AREAS',
    elevators: 'ELEVATORS',
    underground: 'UNDERGROUND AREAS',
    outside: 'EXTERIOR & GROUNDS',
    misc_others: 'MISC / OTHERS',
  };

  const totalPending = Object.values(pendingUploads).reduce((sum, a) => sum + a.length, 0);

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        toggle={handleClose}
        className="modal-fullscreen"
        scrollable
      >
        <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ backgroundColor: '#0D568F' }}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h5 className="mb-0 text-white">
              {isReadOnly
                ? 'View Completed Inspection'
                : isEdit
                  ? 'Edit Inspection'
                  : 'New Building Inspection'}
            </h5>
            {!isReadOnly && <AutosaveStatus status={autosave.status} />}
          </div>
          <img src="/images/rentdigicare-logo.svg" alt="logo" style={{ height: '34px' }} />
        </div>

        <ModalBody style={{ padding: '1.5rem' }}>
          {isReadOnly && (
            <Alert color="info" className="mb-4">
              <span className='text-black'><i className="bx bx-lock-alt me-2"></i>Completed Inspection:</span>
              <span className='ms-1'>This inspection has been finalized. You can view details or download the PDF report.</span>
            </Alert>
          )}

          {!isValidProperty ? (
            <Alert color="warning">Please select a property first</Alert>
          ) : (
            <>
              <Row className="mb-4 align-items-end">
                <Col md={6} lg={4}>
                  <label className="fw-bold">
                    Inspection Date <span className="text-danger">*</span>
                  </label>
                  <Input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={
                      formik.values.inspection_date
                        ? new Date(formik.values.inspection_date).toISOString().split('T')[0]
                        : ''
                    }
                    onChange={e =>
                      formik.setFieldValue('inspection_date', e.target.value ? new Date(e.target.value) : null)
                    }
                    invalid={formik.touched.inspection_date && !!formik.errors.inspection_date}
                    disabled={isReadOnly || (isEdit && userRole !== 'company')}
                  />
                </Col>
              </Row>

              {Object.entries(sectionTitles).map(([key, title], idx) => (
                <div key={key} className="mb-4">
                  <div
                    className="d-flex justify-content-between align-items-center p-2 mb-2 rounded"
                    style={{ cursor: 'pointer', background: openAccordion.includes(idx) ? '#f1f5f9' : 'transparent' }}
                    onClick={() => setOpenAccordion(prev => prev.includes(idx) ? prev.filter(x => x !== idx) : [...prev, idx])}
                  >
                    <h6 className="mb-0 fw-bold">
                      {title}
                      {formik.values[key]?.length > 0 && (
                        <span className="badge bg-primary ms-2">{formik.values[key].length}</span>
                      )}
                    </h6>
                    <FeatherIcon icon={openAccordion.includes(idx) ? 'chevron-up' : 'chevron-down'} size={18} />
                  </div>

                  <Collapse isOpen={openAccordion.includes(idx)}>
                    <div className="building-inspection-table-container">
                      <table className="building-inspection-table">
                        <thead>
                          <tr>
                            <th>CATEGORY</th>
                            <th>NEEDS WORK</th>
                            <th>DUE DATE</th>
                            <th>STATUS</th>
                            <th>NOTES</th>
                            <th>ATTACHMENTS</th>
                            <th>MEMOS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formik.values[key] || []).map((item, i) => (
                            <InspectionRow
                              key={i}
                              section={key}
                              itemIndex={i}
                              item={item}
                              formik={formik}
                              pendingUploads={pendingUploads}
                              setPendingUploads={setPendingUploads}
                              signedUrlCache={signedUrlCache}
                              setSelectedMedia={setSelectedMedia}
                              isCustomItem={!!item.is_custom}
                              isEdit={isEdit}
                              userRole={userRole}
                              userId={userId}
                              isReadOnly={isReadOnly}
                            />
                          ))}
                          {(!isEdit || userRole === 'company') && !isReadOnly && (
                            <tr>
                              <td colSpan={7} className="text-center py-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => addNewItem(key)}
                                >
                                  <FeatherIcon icon="plus" size={14} className="me-1" />
                                  Add Custom Item
                                </button>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Collapse>
                </div>
              ))}

              {/* Conclusions & Recommendations */}
              <div className="mb-4">
                <label className="fw-bold">
                  Conclusions & Recommendations <span className="text-danger">*</span>
                </label>
                <Input
                  type="textarea"
                  rows={4}
                  placeholder="Your conclusions and recommendations..."
                  value={formik.values.conclusions_recommendations || ''}
                  onChange={e => formik.setFieldValue('conclusions_recommendations', e.target.value)}
                  invalid={formik.touched.conclusions_recommendations && !!formik.errors.conclusions_recommendations}
                  disabled={isReadOnly || (isEdit && userRole !== 'company')}
                />
              </div>

              {totalPending > 0 && (
                <Alert color="info" className="mb-3">
                  <i className="bx bx-cloud-upload me-2"></i>
                  {draftId
                    ? `${totalPending} file(s) uploading in the background`
                    : `${totalPending} file(s) will upload as soon as the draft is saved`}
                </Alert>
              )}

              {uploading && uploadStatus && (
                <Alert color="primary">
                  <div className="d-flex align-items-center mb-2">
                    <Spinner size="sm" className="me-2" />
                    Uploading {uploadStatus.current}/{uploadStatus.total}...
                  </div>
                  <Progress value={(uploadStatus.current / uploadStatus.total) * 100} />
                  <small className="d-block mt-1 text-muted">{uploadStatus.currentFile}</small>
                </Alert>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          {isValidProperty && (
            <>
              {formik.values?._id && (
                <DownloadInspectionPDFButton inspectionId={formik.values._id} disabled={uploading} />
              )}

              {!isReadOnly && (
                <>
                  <Button
                    color="light"
                    outline
                    onClick={() => handleSave(false)}
                    disabled={uploading || formik.isSubmitting}
                  >
                    <i className="bx bx-save me-1"></i>
                    {isEdit ? "Save Progress" : "Save as Draft"}
                  </Button>

                  <Button
                    color="primary"
                    onClick={handleFinalize}
                    disabled={uploading || formik.isSubmitting}
                  >
                    {uploading || formik.isSubmitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {uploading ? 'Saving...' : 'Processing...'}
                      </>
                    ) : isEdit ? (
                      'Update & Mark Complete'
                    ) : (
                      <>
                        <i className="bx bx-check-circle me-1"></i>
                        Create & Finalize
                      </>
                    )}
                  </Button>
                </>
              )}

              <Button
                color="secondary"
                onClick={handleClose}
                disabled={uploading || closing}
              >
                {isReadOnly ? 'Close' : closing ? 'Closing…' : 'Close'}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      <MediaPreviewModal
        isOpen={!!selectedMedia}
        toggle={() => setSelectedMedia(null)}
        media={selectedMedia}
      />
    </>
  );
};

export { GenerateBuildingInspectionModal };